export type Gender = 'female' | 'male' | 'trans';
export type StyleType = 'realistic' | 'anime' | 'furry';
export type Ethnicity =
  | 'african'
  | 'european'
  | 'asian'
  | 'latina'
  | 'middle-eastern'
  | 'scandinavian';
export type SkinTone =
  | 'ivory'
  | 'light'
  | 'tan'
  | 'caramel'
  | 'brown'
  | 'deep'
  | 'deep-dark';
export type EyeColor =
  | 'black'
  | 'brown'
  | 'hazel'
  | 'red'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'white'
  | 'gray';
export type HairColor =
  | 'black'
  | 'brown'
  | 'auburn'
  | 'red'
  | 'blonde'
  | 'green'
  | 'blue'
  | 'purple'
  | 'silver'
  | 'pink'
  | 'white'
  | 'gray';
export type HairStyle =
  | 'long'
  | 'short'
  | 'medium'
  | 'wavy'
  | 'curly'
  | 'straight'
  | 'bob'
  | 'custom';
export type BodyType = 'petite' | 'slim' | 'athletic' | 'voluptuous' | 'curvy';
export type ChestSize = 'flat' | 'small' | 'medium' | 'large' | 'huge';
export type BodyShape = 'small' | 'athletic' | 'medium' | 'large' | 'huge';
export type DickSize = 'small' | 'average' | 'medium' | 'large' | 'huge';
export type RelationshipStatus =
  | 'romantic-partner'
  | 'flirty-companion'
  | 'close-friend'
  | 'casual-chat';
export type PersonalityTrait =
  | 'confident'
  | 'playful'
  | 'dominant'
  | 'caring'
  | 'teasing';

// Style-specific types
export type ClothingStyle =
  | 'casual'
  | 'elegant'
  | 'sporty'
  | 'lingerie'
  | 'swimsuit'
  | 'naked'
  | 'custom';
export type AnimeArchetype =
  | 'schoolgirl'
  | 'catgirl'
  | 'warrior'
  | 'idol'
  | 'demon'
  | 'angel'
  | 'custom';
export type AnimeCostume =
  | 'school-uniform'
  | 'maid'
  | 'bikini'
  | 'fantasy-armor'
  | 'kimono'
  | 'casual-anime'
  | 'bunny-suit'
  | 'nurse'
  | 'naked'
  | 'custom';
export type FurryAccessory =
  | 'collar'
  | 'scarf'
  | 'armor'
  | 'hoodie'
  | 'stripes'
  | 'spots'
  | 'naked'
  | 'custom';

export interface CharacterConfig {
  // Step 1
  gender: Gender;
  style: StyleType;
  faceFocus: boolean;
  bodyTypeToggle: boolean;
  ageVerified: boolean;
  safeMode: boolean;
  aesthetic: string;

  // Step 2 (Realistic: Ethnicity & Age, Anime: Archetype & Age, Furry: Species & Fur)
  ethnicity: Ethnicity;
  skinTone: SkinTone;
  // Optional custom skin-tone hex from the "Custom" swatch; when set it
  // overrides the preset `skinTone` for display/generation.
  customSkinTone?: string;
  age: number;

  // Step 3
  eyeColor: EyeColor;
  hairColor: HairColor;
  hairStyle: HairStyle;

  // Step 4
  bodyType: BodyType;
  chestSize: ChestSize;
  bodyShape: BodyShape;
  dickSize?: DickSize;

  // Step 5 (Style-specific: Clothing / Costume / Accessories)
  clothingStyle?: ClothingStyle;
  clothingCustom?: string;
  animeArchetype?: AnimeArchetype;
  animeArchetypeCustom?: string;
  animeCostume?: AnimeCostume;
  animeCostumeCustom?: string;
  furryAccessory?: FurryAccessory;
  furryAccessoryCustom?: string;

  // Premium custom overrides per step
  customEthnicity?: string;
  customHairStyle?: string;
  customBodyType?: string;

  // Step 6 (Personality)
  name: string;
  personality: string;
  personalityTraits: PersonalityTrait[];
  relationshipStatus: RelationshipStatus;
  relationship: string;
  occupation: string;
  voice: string;
  language: string;

  // Furry-specific (only when style === 'furry')
  species?: string;
  furColor?: string;
}

export const DEFAULT_CHARACTER_CONFIG: CharacterConfig = {
  gender: 'female',
  style: 'realistic',
  faceFocus: false,
  bodyTypeToggle: false,
  ageVerified: true,
  safeMode: false,
  aesthetic: 'Aesthetic',
  ethnicity: 'european',
  skinTone: 'light',
  age: 21,
  eyeColor: 'blue',
  hairColor: 'blonde',
  hairStyle: 'long',
  bodyType: 'slim',
  chestSize: 'medium',
  bodyShape: 'medium',
  dickSize: 'average',
  clothingStyle: 'casual',
  animeArchetype: 'schoolgirl',
  animeCostume: 'school-uniform',
  furryAccessory: 'collar',
  name: '',
  personality: '',
  personalityTraits: [],
  relationshipStatus: 'romantic-partner',
  relationship: '',
  occupation: '',
  voice: '',
  language: 'English',
};
